import {Image} from '@jobseeker/lotus/components/Image';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {shallowEqual, useSelector} from 'react-redux';
import {useEffect, useRef, useState} from 'react';
import Typed from 'typed.js';
import {Box, Button, styled, Typography, useTheme} from '@mui/material';

import {
  BgCurve,
  JobseekerLogin,
  Mobile,
  RecLogin,
} from '@jobseeker/lotus/assets/images';

import {
  handleTracking,
  openUrl,
  TrackEventUBA,
} from '@jobseeker/common-core/utils';
import {useRouter} from 'next/router';

import {
  getRefTagAndOrigin,
  HomeCategory,
} from '@components/HomeV2/homeCategory';

import {words} from '@components/HomeV2/constants';
import HomePageFooter from '@components/HomeV2/homePageFooter';
import CompanyJobListingSeoLinkages from '@components/HomeV2/companySeo';
import {Static} from '@components/HomeV2/static';
import HomePageCompanyIcons from '@components/HomeV2/homePageCompanyJobs';
import HomePageJobs from '@components/HomeV2/homePageJobs';
import {JobSearchBar} from '@components/HomeV2/homePageSearch';
import dynamic from 'next/dynamic';
import {Loader} from '@jobseeker/lotus/components';
import {HamburgerMenuNew} from '@jobseeker/lotus/assets/icons';
import MenuDrawer from '@jobseeker/lotus/components/MenuDrawer';
import useInterceptor from '@jobseeker/common-core/hooks/useInterceptor';
import {HOME} from '@constants';

import type {AppState} from '@jobseeker/common-core/types';
import {getKeyFromPath} from '@utils';
import TalentBridgeTopBanner from '../TalentBridge/TalentBridgeTopBanner';

const Img = styled(Image)(({theme}) => ({
  position: 'absolute',
  left: 0,
  width: '100%',
  top: 0,
  height: 705,
  zIndex: -1,

  [theme.breakpoints.up('lg')]: {
    height: 770, // min-width: 1280px
  },
  [theme.breakpoints.down('md')]: {
    height: 1023, // max-width: 1023px
  },
  [theme.breakpoints.down('sm')]: {
    height: 650, // max-width: 430px
  },
}));

const HiristAuthentication = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/HiristAuthenticationHarness/AuthForm'
    ),
  {
    ssr: false,
  }
);

const Text = styled(Typography)(({theme}) => ({
  textAlign: 'center',
  margin: '0 auto',
  marginTop: '15px',
  marginBottom: '0px',
  lineHeight: '57px',
  fontSize: '35px',
  fontWeight: 600,
  color: '#000000',
  width: '793px',
  fontFamily: 'Nunito Sans',
  [theme.breakpoints.down(1023)]: {
    fontSize: '40px',
    lineHeight: 1.23,
    width: '78%',
    marginBottom: '31px',
    minHeight: '98px',
  },
  [theme.breakpoints.down(599)]: {
    width: '78%',
  },
  [theme.breakpoints.between(425, 599)]: {
    minHeight: '147px',
    marginTop: '30px',
  },
  [theme.breakpoints.down(484)]: {
    fontSize: '26px',
    marginTop: '30px',
    marginBottom: '10px',
    minHeight: '93px',
    width: '88%',
  },
}));

const CookieBanner = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/NavigationHarness/CookieBanner'
    ),
  {
    loading: () => <Loader />,
  }
);

const HomeV2 = ({
  blogs,
  sortedJobs,
}: {
  blogs: object;
  sortedJobs: Array<object>;
}) => {
  const {device} = useSelector(
    (state: AppState) => state.app.layout,
    shallowEqual
  );

  const [loginOpen, setLoginOpen] = useState<boolean>(false);
  const searchFormValue = useSelector((state: AppState) => state.job.filters);

  console.log({searchFormValue});

  const theme = useTheme();

  const [openMenuDrawer, setMenuDrawerOpen] = useState(false);

  const handleMenuDrawer = (open: boolean) => {
    setMenuDrawerOpen(open);
  };

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const interceptor: any = useInterceptor();

  const {
    header: {
      meta: {tabsList},
    },
  } = interceptor;

  const userId = '-1';
  const [url, setUrl] = useState(process.env.NEXT_PUBLIC_LOAD_CONFIG);
  const router = useRouter();

  useEffect(() => {
    setUrl(window.location.href);
  }, []);

  const urlObj = new URL(url ?? '');
  const queryParams = Object.fromEntries(urlObj.searchParams.entries());

  useEffect(() => {
    if (router?.pathname?.includes('/register')) {
      TrackEventUBA('registrationInitialOverlayPageView', 'view', {
        ref: queryParams.ref || '',
        sessionUsername: userId ? userId.toString() : '',
      });
    }
  }, []);

  const handleUbaTracking = (eventName: string, section: string) => {
    TrackEventUBA(eventName, 'click', {
      page: 'jsHomePage',
      section,
      ref: queryParams.ref || '',
      sessionUsername: userId ? userId.toString() : '',
    });
  };

  const handleLogoClick = () => {
    const {origin, refText} = getRefTagAndOrigin();
    handleTracking('jsClickMainLogo', {
      event_category: 'jsHomePage',
      Source: `${origin}`,
      Device: sessionStorage.getItem('device') || 'desktop',
      Ref: `${refText}`,
    });
    handleUbaTracking('jsHomePageClick', 'hiristLogo');
    window.location.href = '/';
  };

  const recruiterLogin = () => {
    const {origin, refText} = getRefTagAndOrigin();
    handleTracking(
      'jsClickRecruiterLogin',

      {
        event_category: 'jsHomePage',
        Source: `${origin}`,
        Device: sessionStorage.getItem('device') || 'desktop',
        Ref: `${refText}`,
      }
    );
    handleUbaTracking('jsHomePageClick', 'RecruiterLogin');
    openUrl(`https://recruit${process.env.NEXT_PUBLIC_DOMAIN}/login`, '_blank');
  };

  const userLogin = () => {
    const {origin, refText} = getRefTagAndOrigin();
    handleTracking(
      'jsClickJobseekerLogin',

      {
        event_category: 'jsHomePage',
        Source: `${origin}`,
        Device: sessionStorage.getItem('device') || 'desktop',
        Ref: `${refText}`,
      }
    );
    handleUbaTracking('jsHomePageClick', 'JobseekerLogin');
    TrackEventUBA('jsLoginLogoView', 'view', {
      page: 'jsHomePage',
      section: 'signup',
      ref: queryParams.ref || '',
      sessionUsername: userId ? userId.toString() : '',
    });
    TrackEventUBA('registrationInitialPageView', 'view', {
      ref: getKeyFromPath('ref') || '',
      pageName: 'Registration Initial page',
      sessionUsername: sessionStorage.getItem('userId') || '',
    });
    setLoginOpen(true);
    // this.props.openLoginPopup();
  };

  const onAppDownloadClick = () => {
    handleTracking(
      'jsClickDwdAppBeforeLogin',

      {
        event_category: 'MobileAppPromotion',
        Origin: 'LandingPage',
        Device: sessionStorage.getItem('device') || 'desktop',
        Status: 'LoggedOut',
      }
    );
    handleUbaTracking('jsHomePageClick', 'DownloadApp');
    openUrl('/downloadapp?ref=landingpage', '_self');
  };

  const PageContentWrapper = styled('div')(({theme}) => ({
    width: '100%',
    height: '100%',

    zIndex: 5,
    position: 'relative',
    top: 0,
    marginBottom: true ? '0px' : '0',
    [theme.breakpoints.up('md')]: {
      marginBottom: '0px',
    },

    // '&::before': {
    //   content: '""',
    //   position: 'absolute',
    //   top: 0,
    //   left: 0,
    //   width: '100%',
    //   height: '700px', // Fixed height for background image
    //   backgroundRepeat: 'no-repeat',
    //   backgroundPosition: 'top left',
    //   backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1440' height='698' viewBox='0 0 1440 698' fill='none'%3E%3Cpath opacity='0.9' fill-rule='evenodd' clip-rule='evenodd' d='M0 0H1440V837.932C1440 837.932 662.272 908 400.5 908C138.728 908 0 803.496 0 803.496V0Z' fill='%23FFFAF0'/%3E%3C/svg%3E")`,
    //   backgroundSize: 'cover', // Ensures the image covers the fixed height
    //   zIndex: -1, // Puts it behind the content
    // },

    [theme.breakpoints.up('md')]: {
      '&::before': {
        height: '450px', // Adjust for larger screens
      },
    },
  }));
  // Inline styles
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const cookieBannerData = {
    policyLinks: {
      privacyPolicy: 'https://www.iimjobs.com/privacy/',
      cookiePolicy: 'https://www.iimjobs.com/cookiepolicy',
    },
    CTA: 'Got it',
  };
  const el = useRef<HTMLSpanElement>(null); // Ref for the DOM element
  const typedInstance = useRef<Typed | null>(null);
  const isAppHirist = process.env.NEXT_PUBLIC_APP_NAME === 'hirist';
  useEffect(() => {
    if (!words?.length) return; // Wait until words are available

    const timer = setTimeout(() => {
      if (el.current) {
        el.current.innerText = words[0];

        typedInstance.current?.destroy();
        typedInstance.current = new Typed(el.current, {
          strings: words,
          showCursor: true,
          typeSpeed: 60,
          backSpeed: 60,
          loop: true,
        });
      }
    }, 500); // Small delay to ensure mounting

    return () => {
      clearTimeout(timer);
      typedInstance.current?.destroy();
    };
  }, [words]);

  return (
    <>
      {isAppHirist && <TalentBridgeTopBanner />}
      <HiristAuthentication
        variant={VersionVariants.VERSION_TWO}
        openDialog={loginOpen}
        setOpenDialog={() => {
          setLoginOpen(false);
        }}
      />
      <PageContentWrapper>
        <Img src={BgCurve as unknown as string} alt="background image" />

        <Box
          sx={{padding: {xxs: '18px 0px 0px 15px', md: '22px 70px 0px'}}}
          display="flex"
          justifyContent={{xxs: 'flex-start', md: 'space-between'}}
        >
          <Box
            display={{xxs: 'block', lg: 'none'}}
            mr={theme.spacing(2)}
            pt="11px"
            onClick={() => {
              handleMenuDrawer(true);
            }}
          >
            <Image
              src={HamburgerMenuNew}
              alt={'HamburgerMenu'}
              data-testid="menu_mobile"
              data-name="menu_mobile"
              width={24}
              height={24}
              objectFit="cover"
              quality={50}
              style={{marginRight: 0.5, verticalAlign: 'top'}}
            />
          </Box>

          {openMenuDrawer && (
            <MenuDrawer
              theme={theme}
              handleDrawer={handleMenuDrawer}
              openDrawer={openMenuDrawer}
              name={''}
              email={''}
              phoneVerified={false}
              profileProgress={98}
              profilePicUrl={''}
              proMembership={undefined}
              OnLogoutBtnClick={() => {}}
              followUps={undefined}
              profileDrawerOptions={undefined}
              tabsList={tabsList}
              aboutSection={[]}
              proMeta={{refCode: '', refFeature: ''}}
              reCap={0}
              page={HOME}
              savedJobCount={undefined}
              sendEvent={undefined}
              loginHandler={() => {
                setLoginOpen(true);
              }}
              showSpotLight={false}
            />
          )}
          <a
            href={`/`}
            style={{
              textDecoration: 'none',
            }}
          >
            <Image
              src="https://staticlogo.hirist.com/webp/hirist-tech-logo.webp"
              alt="HIRIST.TECH"
              className="sc-kEYyzF iPqUTn"
              height={40}
              width={150}
              onClick={handleLogoClick}
              style={{
                cursor: 'pointer',
                marginTop: '5px',
              }}
            />
          </a>
          <Box display={{xxs: 'none', md: 'flex'}}>
            <Button
              onClick={onAppDownloadClick}
              sx={{
                '&:hover': {
                  backgroundColor: 'transparent', // Use 'transparent' instead of 'none'
                },
              }}
            >
              <Image src={Mobile} alt="down" height={24} width={16} />

              <Typography
                sx={{
                  fontFamily: 'Nunito Sans',
                  fontSize: 16,
                  color: '#e9630c',
                  fontWeight: 500,
                  marginLeft: 1,
                }}
              >
                Download App
              </Typography>
            </Button>

            <Button
              onClick={recruiterLogin}
              sx={{
                border: '1px solid #e9630c',
                borderRadius: '10px',
                marginLeft: 2,
                padding: '11px',
                display: 'flex',
                alignItems: 'center',
                transition: 'background-color 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: '#e9630c',
                  color: '#fff !important',
                  '& img': {
                    filter: 'brightness(0) invert(1)', // Turns image white
                  },
                  '& .text': {
                    color: '#fff', // Changes Typography color
                  },
                },
              }}
            >
              <Image
                src={RecLogin}
                alt="down"
                height={20}
                width={20}
                style={{transition: 'filter 0.3s ease-in-out'}}
              />

              <Typography
                className="text" // Assigning class for better targeting
                sx={{
                  fontFamily: 'Nunito Sans',
                  fontSize: 16,
                  color: '#e9630c',
                  fontWeight: 500,
                  marginLeft: 1,
                  transition: 'color 0.3s ease-in-out',
                }}
              >
                Recruiter Login
              </Typography>
            </Button>

            <Button
              sx={{
                backgroundColor: '#e9630c',
                borderRadius: '10px',
                marginLeft: 2,
                padding: '11px',
                display: 'flex',
                border: '1px solid #e9630c',
                alignItems: 'center',
                transition: 'background-color 0.3s ease-in-out',
                '&:hover': {
                  border: '1px solid #e9630c',
                  backgroundColor: 'transparent',
                  '& img': {
                    filter:
                      'brightness(0.0) invert(38%) sepia(98%) saturate(750%) hue-rotate(-5deg)',
                  },
                  '& .text': {
                    color: '#e9630c', // Changes Typography color
                  },
                },
              }}
              onClick={userLogin}
            >
              <Image
                src={JobseekerLogin as unknown as string}
                alt="down"
                height={20}
                width={20}
                style={{transition: 'filter 0.3s ease-in-out'}} // Smooth effect
              />

              <Typography
                className="text"
                sx={{
                  fontFamily: 'Nunito Sans',
                  fontSize: 16,
                  color: 'white',
                  fontWeight: 500,
                  marginLeft: 1,
                  transition: 'color 0.3s ease-in-out',
                }}
              >
                Jobseeker Login
              </Typography>
            </Button>
          </Box>
        </Box>
        {device == 'desktop' ? (
          <Text
            style={{
              fontWeight: '600',
              fontFamily: 'Nunito Sans',
            }}
          >
            Handpicked Premium{' '}
            <b
              style={{
                fontWeight: '600',
                color: '#e9630c',
                fontFamily: 'Nunito Sans',
              }}
              ref={el}
            />
            {' Jobs'}
          </Text>
        ) : (
          <Text>
            Handpicked Premium <br />
            <b
              style={{
                fontWeight: '600',
                color: '#e9630c',
                fontFamily: 'Nunito Sans',
              }}
            >
              <b
                style={{
                  fontWeight: '600',
                  color: '#e9630c',
                  fontFamily: 'Nunito Sans',
                }}
                ref={el}
              />
            </b>
            <br /> {' Jobs'}
          </Text>
        )}
        <Box
          width={{xxs: '87%', md: '75%', lg: '55%'}}
          sx={{
            backgroundColor: {md: '#fff'},
            p: {md: 1.5},
            pb: {md: 1},
            pr: {md: 2.5},
            borderRadius: {md: 2},
            boxShadow: {md: '0 10px 50px 0 rgba(0,0,0,0.07)'},
            border: {xxs: 0, md: 1},
            borderColor: '#ebebeb !important',
          }}
          alignItems="center"
          mx="auto"
          mb="48px"
          mt="12px"
        >
          <JobSearchBar
            variant={VersionVariants.VERSION_TWO}
            showSortBy={false}
            isFilter={false}
            showSearchInput={true}
            formValue={searchFormValue}
            inline={true}
            onlySeachInput={false}
            industryOptions={[]}
          />
        </Box>
        <HomeCategory loginOpen={loginOpen} />
        <HomePageCompanyIcons isTechDrive={false} />

        <HomePageJobs sortedJobs={sortedJobs} />
        <CompanyJobListingSeoLinkages source="homepage" />

        <Static
          isTechDrive={false}
          setLoginPopUp={setLoginOpen}
          blogs={blogs}
        />
        <HomePageFooter />
      </PageContentWrapper>
      <CookieBanner
        showCookieBanner={showCookieBanner}
        setShowCookieBanner={setShowCookieBanner}
        policyLinks={cookieBannerData.policyLinks}
        CTA={cookieBannerData.CTA}
      />
    </>
  );
};

export default HomeV2;
